import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, Activity, TrendingUp, Globe, BarChart3, 
  Lightbulb, Eye, Calendar, MapPin, Heart
} from 'lucide-react';

const AnalyticsTabs = ({ analyses, darkMode }) => {
  const [selectedEmotion, setSelectedEmotion] = useState('all');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');

  // Calculate metrics
  const metrics = {
    byEmotion: {},
    byAgeGroup: {
      '13-24': 0, // Gen Z
      '25-40': 0, // Millennials
      '41-56': 0, // Gen X
      '57-75': 0  // Boomers
    },
    byGender: {
      'Male': 0,
      'Female': 0,
      'All': 0
    },
    byRegion: {},
    byCountry: {},
    totalAnalyses: analyses.length,
    avgValence: 0,
    avgArousal: 0,
    avgDominance: 0
  };

  // Process analyses
  analyses.forEach(analysis => {
    // Emotions
    const emotion = analysis.dominant_emotion || 'unknown';
    metrics.byEmotion[emotion] = (metrics.byEmotion[emotion] || 0) + 1;

    // Age groups from audience reactions
    if (analysis.audience_reactions) {
      Object.keys(analysis.audience_reactions).forEach(key => {
        if (key.includes('18-24')) metrics.byAgeGroup['13-24']++;
        if (key.includes('25-40')) metrics.byAgeGroup['25-40']++;
        if (key.includes('41-56')) metrics.byAgeGroup['41-56']++;
        if (key.includes('57-75')) metrics.byAgeGroup['57-75']++;
      });
    }

    // Gender
    const gender = analysis.audience_filters?.custom?.gender || 'All';
    metrics.byGender[gender] = (metrics.byGender[gender] || 0) + 1;

    // Region/Country
    const region = analysis.audience_filters?.region || 'World';
    const country = analysis.audience_filters?.custom?.country || 'Unknown';
    metrics.byRegion[region] = (metrics.byRegion[region] || 0) + 1;
    metrics.byCountry[country] = (metrics.byCountry[country] || 0) + 1;

    // Averages
    metrics.avgValence += analysis.valence || 0;
    metrics.avgArousal += analysis.arousal_level || 0;
    metrics.avgDominance += analysis.dominance || 0;
  });

  metrics.avgValence = (metrics.avgValence / analyses.length).toFixed(2);
  metrics.avgArousal = (metrics.avgArousal / analyses.length).toFixed(2);
  metrics.avgDominance = (metrics.avgDominance / analyses.length).toFixed(2);

  // Filter analyses
  const filteredAnalyses = analyses.filter(a => {
    if (selectedEmotion !== 'all' && a.dominant_emotion !== selectedEmotion) return false;
    if (selectedGender !== 'all' && a.audience_filters?.custom?.gender !== selectedGender) return false;
    // Add age group filtering logic here if needed
    return true;
  });

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className={`grid w-full grid-cols-5 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="emotions">Emotions</TabsTrigger>
        <TabsTrigger value="demographics">Demographics</TabsTrigger>
        <TabsTrigger value="geography">Geography</TabsTrigger>
        <TabsTrigger value="trends">Trends</TabsTrigger>
      </TabsList>

      {/* Overview Tab */}
      <TabsContent value="overview" className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-600" />
                Total Analyses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {metrics.totalAnalyses.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Heart className="w-4 h-4 text-pink-600" />
                Avg Valence
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {(metrics.avgValence * 100).toFixed(0)}%
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-orange-600" />
                Avg Arousal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {(metrics.avgArousal * 100).toFixed(0)}%
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-purple-600" />
                Avg Dominance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {(metrics.avgDominance * 100).toFixed(0)}%
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Emotions Tab */}
      <TabsContent value="emotions" className="space-y-4">
        <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <CardHeader>
            <CardTitle>Emotion Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(metrics.byEmotion)
                .sort((a, b) => b[1] - a[1])
                .map(([emotion, count]) => (
                  <div 
                    key={emotion}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedEmotion === emotion 
                        ? 'border-blue-600 bg-blue-50' 
                        : darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                    }`}
                    onClick={() => setSelectedEmotion(selectedEmotion === emotion ? 'all' : emotion)}
                  >
                    <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {count}
                    </div>
                    <div className={`text-sm capitalize ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {emotion}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {((count / metrics.totalAnalyses) * 100).toFixed(1)}%
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Demographics Tab */}
      <TabsContent value="demographics" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader>
              <CardTitle>Age Groups</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(metrics.byAgeGroup).map(([range, count]) => (
                <div key={range} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                      {range === '13-24' && 'Gen Z (13-24)'}
                      {range === '25-40' && 'Millennials (25-40)'}
                      {range === '41-56' && 'Gen X (41-56)'}
                      {range === '57-75' && 'Boomers (57-75)'}
                    </span>
                    <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(count / metrics.totalAnalyses) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader>
              <CardTitle>Gender Distribution</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(metrics.byGender).map(([gender, count]) => (
                <div 
                  key={gender}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedGender === gender
                      ? 'bg-purple-100 border-2 border-purple-600'
                      : darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                  onClick={() => setSelectedGender(selectedGender === gender ? 'all' : gender)}
                >
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {gender}
                    </span>
                    <div className="text-right">
                      <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {count}
                      </div>
                      <div className="text-xs text-gray-500">
                        {((count / metrics.totalAnalyses) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Geography Tab */}
      <TabsContent value="geography" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                By Region
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(metrics.byRegion)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([region, count]) => (
                    <div key={region} className="flex justify-between items-center">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {region}
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-600" />
                By Country
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(metrics.byCountry)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([country, count]) => (
                    <div key={country} className="flex justify-between items-center">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {country}
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Trends Tab */}
      <TabsContent value="trends" className="space-y-4">
        <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              Key Insights
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
              <div className="font-bold text-blue-600 mb-2">Most Common Emotion</div>
              <div className={`text-xl capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {Object.entries(metrics.byEmotion).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
              </div>
            </div>

            <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
              <div className="font-bold text-green-600 mb-2">Most Active Region</div>
              <div className={`text-xl ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {Object.entries(metrics.byRegion).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
              </div>
            </div>

            <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
              <div className="font-bold text-purple-600 mb-2">Overall Sentiment</div>
              <div className={`text-xl ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {metrics.avgValence > 0.6 ? '😊 Positive' : metrics.avgValence > 0.4 ? '😐 Neutral' : '😟 Negative'}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default AnalyticsTabs;
